import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { REHYDRATE } from "redux-persist"

/**
 * On the web every tab keeps its own store but they all persist to the same
 * localStorage entry. A tab left on a logged-out screen while another tab
 * logs in would otherwise stay logged out, and its next write would put the
 * logged-out state back over the new session. So a tab adopts a NEW login
 * made in another tab — more logged in than itself, with a token it has never
 * held. Nothing else crosses: a logout, a sibling picked in another tab, or a
 * session this tab just left does not pull it anywhere.
 */

// The `data` keys that make a session. Nothing else is adopted, so a tab's
// own work in progress (a funnel, a form) survives.
const SESSION_KEYS = ["session", "authTokens", "currentUser", "family"]

export const NOTICE_MS = 1200

export const SESSION_RANK = { NONE: 0, ACCOUNTS: 1, OPEN: 2 }

/** Accounts kept on the device, to pick from (« Qui utilise Masteur ? »). */
export const hasStoredAccounts = (data) =>
    Object.keys(data?.authTokens?.storedTokens ?? {}).length > 0

/** 0 logged out, 1 accounts to pick from, 2 an account open. */
export const sessionRank = (data) => {
    if (data?.session?.object?.token) return SESSION_RANK.OPEN
    if (hasStoredAccounts(data)) return SESSION_RANK.ACCOUNTS
    return SESSION_RANK.NONE
}

/** The session part of a raw `persist:root` value, or null. */
export const sessionFromPersistedRoot = (raw) => {
    try {
        const data = JSON.parse(JSON.parse(raw).data)
        return Object.fromEntries(
            SESSION_KEYS.filter((key) => key in data).map((key) => [
                key,
                data[key],
            ])
        )
    } catch {
        return null
    }
}

/** Where a password login would land for this session. */
export const landOnSession = (navigation, rank) => {
    if (rank === SESSION_RANK.ACCOUNTS)
        navigation.replace("family-account-selection")
    else if (rank === SESSION_RANK.OPEN) navigation.replace("menu")
}

const waitingScreens = new Set()

/**
 * A logged-out screen that should say « Vous êtes connecté » before moving
 * on: `announce()` when another tab logs in, `land(rank)` once the session is
 * adopted, NOTICE_MS later.
 */
export const waitForSessionFromAnotherTab = (screen) => {
    waitingScreens.add(screen)
    return () => {
        waitingScreens.delete(screen)
    }
}

/**
 * Opened on a logged-out screen while this browser already holds a session
 * (a reload, a second tab): land like a password login would. Only when the
 * screen is the entry point — reached from the account picker (an expired
 * account signing in again), it must stay.
 */
export const useLandIfAlreadyLoggedIn = (navigation) => {
    const rank = useSelector((state) => sessionRank(state.data))
    useEffect(() => {
        const { routes = [], index = 0 } = navigation.getState() ?? {}
        const isEntry = routes
            .slice(0, index)
            .every((route) => route.name === "menu")
        if (isEntry) landOnSession(navigation, rank)
    }, [])
}

/** True while « Vous êtes connecté » is shown, before landing. */
export const useSessionFromAnotherTab = (navigation) => {
    const [connected, setConnected] = useState(false)
    useEffect(
        () =>
            waitForSessionFromAnotherTab({
                announce: () => setConnected(true),
                land: (rank) => {
                    if (rank === SESSION_RANK.NONE) setConnected(false)
                    else landOnSession(navigation, rank)
                },
            }),
        [navigation]
    )
    return connected
}

const tokensOf = (data) =>
    [
        data?.session?.object?.token,
        ...Object.values(data?.authTokens?.storedTokens ?? {}).map(
            (account) => account?.token
        ),
    ].filter(Boolean)

export const syncSessionAcrossTabs = (
    store,
    persistKey,
    target = globalThis.window
) => {
    if (typeof target?.addEventListener !== "function") return () => {}
    const storage = target.localStorage
    const readPersisted = () =>
        sessionFromPersistedRoot(storage?.getItem(`persist:${persistKey}`))

    const heldTokens = new Set()
    const rememberTokens = () =>
        tokensOf(store.getState().data).forEach((token) =>
            heldTokens.add(token)
        )
    rememberTokens()
    const stopRemembering = store.subscribe(rememberTokens)

    const isNewLogin = (incoming) =>
        !!incoming &&
        sessionRank(incoming) > sessionRank(store.getState().data) &&
        tokensOf(incoming).some((token) => !heldTokens.has(token))

    let latest = null
    let pending = null

    /** The rank this tab now has, NONE when nothing was adopted. */
    const adopt = () => {
        pending = null
        if (!isNewLogin(latest)) return SESSION_RANK.NONE
        // autoMergeLevel2 merges these keys over the tab's own `data`.
        store.dispatch({
            type: REHYDRATE,
            key: persistKey,
            payload: { data: latest },
        })
        return sessionRank(store.getState().data)
    }

    const onStorage = (event) => {
        // Only the browser's own notice of another tab's write counts, and
        // what it carries is re-read from storage: a script can dispatch a
        // `storage` event with any `newValue`, never a trusted one.
        if (
            !event.isTrusted ||
            event.storageArea !== storage ||
            event.key !== `persist:${persistKey}`
        )
            return
        // Kept even when it is not a login: one that is undone during the
        // notice is not adopted.
        latest = readPersisted()
        if (pending || !isNewLogin(latest)) return

        const screens = [...waitingScreens]
        if (screens.length === 0) {
            adopt()
            return
        }
        screens.forEach((screen) => screen.announce())
        pending = setTimeout(() => {
            const rank = adopt()
            screens
                .filter((screen) => waitingScreens.has(screen))
                .forEach((screen) => screen.land(rank))
        }, NOTICE_MS)
    }

    target.addEventListener("storage", onStorage)
    return () => {
        clearTimeout(pending)
        stopRemembering()
        target.removeEventListener("storage", onStorage)
    }
}
